// Local persistence for the "current filling" pause/hold flow, mirroring
// manifold-pwa's src/utils/fillingQueue.js. CRA is desktop/LAN, not an
// installable offline app, so unlike the PWA version there's no pending-
// upload queue here — just enough to survive a page reload and to let an
// operator hold up to two fillings while working on another.
const activeKey = (username) => `mm_cra_active_filling_${username}`;
const heldKey = (username) => `mm_cra_held_fillings_${username}`;

export function saveActiveFilling(username, state) {
  localStorage.setItem(activeKey(username), JSON.stringify(state));
}

export function loadActiveFilling(username) {
  try {
    const raw = localStorage.getItem(activeKey(username));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearActiveFilling(username) {
  localStorage.removeItem(activeKey(username));
}

// Up to two fillings can be set aside — frozen exactly as they were — while
// a different one is started, and picked back up later. See
// manifold-pwa/src/utils/fillingQueue.js for the full rationale (this is a
// straight port).
export const MAX_HELD_FILLINGS = 2;

export function loadHeldFillings(username) {
  try {
    const raw = localStorage.getItem(heldKey(username));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHeldFillings(username, list) {
  localStorage.setItem(heldKey(username), JSON.stringify(list));
}

export function heldFillingCount(username) {
  return loadHeldFillings(username).length;
}

export function holdFilling(username, filling) {
  const held = loadHeldFillings(username);
  if (held.length >= MAX_HELD_FILLINGS) return false;
  held.push({ id: crypto.randomUUID(), ...filling, heldAt: new Date().toISOString() });
  saveHeldFillings(username, held);
  return true;
}

// Removes a held filling and hands back its "unfrozen" state: the entire
// duration it sat on hold is folded into pausedDurationMs (exactly like
// resuming from a regular pause), so its elapsed clock picks up right where
// it was frozen and starts ticking forward again.
export function resumeHeldFilling(username, id) {
  const held = loadHeldFillings(username);
  const idx = held.findIndex((f) => f.id === id);
  if (idx === -1) return null;

  const item = held[idx];
  const now = Date.now();
  const pausedDurationMs = item.pausedDurationMs + (item.pausedAt ? Math.max(0, now - new Date(item.pausedAt).getTime()) : 0);
  const resumed = { startedAt: item.startedAt, pausedAt: null, pausedDurationMs, forms: item.forms, gas: item.gas };

  held.splice(idx, 1);
  saveHeldFillings(username, held);
  saveActiveFilling(username, resumed);
  return resumed;
}
